import React from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Snackbar, Stack, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BrandLogo from '@src/components/BrandLogo';
import type { AvisoPantalla } from './useExpediente';

/** Etiqueta de sección: mayúsculas espaciadas, el gesto tipográfico de la marca. */
export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="overline" color="text.secondary" display="block" sx={{ lineHeight: 1.6 }}>
        {children}
    </Typography>
);

/** Número del paso, o palomita cuando ya está hecho. */
const Marca: React.FC<{ numero?: number; hecho?: boolean }> = ({ numero, hecho }) => (
    <Box
        sx={{
            flexShrink: 0,
            width: 26,
            height: 26,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: '0.8rem',
            bgcolor: hecho ? 'success.main' : 'primary.main',
            color: hecho ? '#FFFFFF' : 'primary.contrastText'
        }}
    >
        {hecho ? <CheckRoundedIcon sx={{ fontSize: 17 }} /> : numero}
    </Box>
);

/** Una tarjeta: título (con su paso, si lo tiene), una línea de contexto y su contenido. */
export const Seccion: React.FC<{
    titulo: string;
    numero?: number;
    hecho?: boolean;
    detalle?: React.ReactNode;
    accion?: React.ReactNode;
    apagada?: boolean;
    children?: React.ReactNode;
}> = ({ titulo, numero, hecho, detalle, accion, apagada, children }) => {
    const conMarca = numero !== undefined || hecho;
    return (
        <Paper
            variant="outlined"
            sx={{ borderRadius: 3, p: { xs: 2, sm: 2.75 }, opacity: apagada ? 0.5 : 1, transition: 'opacity .2s' }}
        >
            <Stack spacing={children ? 2 : 0}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    {conMarca && <Marca numero={numero} hecho={hecho} />}
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pt: conMarca ? '2px' : 0 }}>
                        <Typography variant="h6" component="h2" sx={{ lineHeight: 1.3 }}>
                            {titulo}
                        </Typography>
                        {detalle && (
                            <Typography variant="body2" color="text.secondary">
                                {detalle}
                            </Typography>
                        )}
                    </Stack>
                    {accion}
                </Stack>
                {children}
            </Stack>
        </Paper>
    );
};

/** Renglón «etiqueta: valor». */
export const Dato: React.FC<{ etiqueta: string; children: React.ReactNode }> = ({ etiqueta, children }) => (
    <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
            {etiqueta}
        </Typography>
        <Typography variant="body2" textAlign="right" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {children}
        </Typography>
    </Stack>
);

/** Cabecera de las dos pantallas: marca, folio y título. */
export const Cabecera: React.FC<{ folio?: string; titulo: string; subtitulo?: React.ReactNode; logo?: boolean }> = ({
    folio,
    titulo,
    subtitulo,
    logo = false
}) => (
    <Stack spacing={0.75} sx={{ pt: { xs: 0.5, sm: 1 } }}>
        {logo && (
            <Box sx={{ pb: 1 }}>
                <BrandLogo size={26} />
            </Box>
        )}
        <SectionTitle>{folio ? `KABANDÚ STUDIO · ${folio}` : 'KABANDÚ STUDIO'}</SectionTitle>
        <Typography variant="h4" component="h1">
            {titulo}
        </Typography>
        {subtitulo && <Typography color="text.secondary">{subtitulo}</Typography>}
    </Stack>
);

/** Tarjeta de «todo listo», centrada. */
export const Listo: React.FC<{ titulo: string; detalle?: React.ReactNode; children?: React.ReactNode }> = ({
    titulo,
    detalle,
    children
}) => (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2.5, sm: 3 } }}>
        <Stack spacing={1.25} alignItems="center" textAlign="center">
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    color: '#FFFFFF',
                    display: 'grid',
                    placeItems: 'center'
                }}
            >
                <CheckRoundedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h6" component="p">
                {titulo}
            </Typography>
            {detalle && (
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                    {detalle}
                </Typography>
            )}
            {children}
        </Stack>
    </Paper>
);

export const Cargando: React.FC = () => (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
        <CircularProgress size={28} />
    </Stack>
);

/** Error al abrir: link vencido (401) o cualquier otro, con el mensaje del server. */
export const ErrorCarga: React.FC<{ mensaje?: string; estado?: number }> = ({ mensaje, estado }) => {
    const vencido = estado === 401;
    return (
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ maxWidth: 480, mx: 'auto', pt: 6 }}>
            <BrandLogo size={36} showName={false} justify="center" />
            <Typography variant="h5" component="h1">
                {vencido ? 'Este link ya no está activo' : 'No pudimos abrir tu expediente'}
            </Typography>
            <Typography color="text.secondary">{mensaje}</Typography>
            {!vencido && (
                <Button variant="outlined" onClick={() => window.location.reload()}>
                    Volver a intentar
                </Button>
            )}
        </Stack>
    );
};

export const AvisoSnackbar: React.FC<{ aviso: AvisoPantalla | null; onClose: () => void }> = ({ aviso, onClose }) => (
    <Snackbar
        open={!!aviso}
        autoHideDuration={aviso?.tipo === 'error' ? 6000 : 3000}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
        {aviso ? (
            <Alert severity={aviso.tipo} variant="filled" onClose={onClose} sx={{ width: '100%' }}>
                {aviso.texto}
            </Alert>
        ) : undefined}
    </Snackbar>
);
